import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Eye, Lock, Activity, Network, Handshake,
  FileSearch, MessageSquare, GitBranch, ShieldCheck, Users,
} from 'lucide-react';
import { RequestAccessModal } from './RequestAccessModal';
import { StickyMobileCTA } from './StickyMobileCTA';
import { useReveal } from '@/lib/useReveal';

/**
 * /for-lenders — conforms to GP CAP Public Site Design System v1.
 * Step labels MUST match the homepage Pathways component exactly:
 *   01 Curated opportunities · 02 NDA-gated data room · 03 Structured response
 *   04 Mandate-fit matching · 05 Relationship intelligence
 */

const PATHWAY_STEPS = [
  {
    icon: Eye,
    title: 'Curated opportunities',
    body: 'You see only opportunities that match your stated mandate — asset class, geography, ticket envelope and product preference. Every invitation reflects a deliberate match. No open feed, no broadcast email.',
  },
  {
    icon: Lock,
    title: 'NDA-gated data room',
    body: 'Each transaction lives behind an executed NDA before any document or borrower detail surfaces. Role-based access controls which sections, files and conversations a counterparty can see.',
  },
  {
    icon: Activity,
    title: 'Structured response',
    body: 'Submit indicative terms through a structured bid form — facility amount, pricing, fees, tenor, recourse, conditions and timeline. Q&A flows in-platform and is mirrored to every active counterparty for consistency.',
  },
  {
    icon: Network,
    title: 'Mandate-fit matching',
    body: 'Repeat scoring across deals improves the relevance of every subsequent introduction. The more accurately your mandate is captured, the more precisely future opportunities arrive.',
  },
  {
    icon: Handshake,
    title: 'Relationship intelligence',
    body: 'Your counterparty profile is retained across mandates — no re-onboarding on every deal. GP Capital remains the single point of contact across the relationship.',
  },
];

const PLATFORM_CONTROLS = [
  {
    icon: Eye,
    title: 'Curated deal flow',
    body: 'Opportunities filtered to your mandate, geography and ticket envelope — never broadcast.',
  },
  {
    icon: Lock,
    title: 'NDA-gated data room',
    body: 'Every transaction lands behind an executed NDA before any document or detail is exposed.',
  },
  {
    icon: FileSearch,
    title: 'Controlled document access',
    body: 'Document permissions and watermarking are set per counterparty by GP Capital — some files are preview-only, others downloadable.',
  },
  {
    icon: MessageSquare,
    title: 'Structured Q&A',
    body: 'Questions, clarifications and responses flow through the platform; consistent information reaches every active counterparty.',
  },
  {
    icon: GitBranch,
    title: 'Mandate-fit matching',
    body: 'Counterparties are matched to opportunities by mandate, region and ticket envelope — not invited by volume.',
  },
  {
    icon: ShieldCheck,
    title: 'Audit-aware workflow',
    body: 'Document access, Q&A and interest events are recorded against each counterparty across the lifecycle.',
  },
];

const PROCESS_NOTES = [
  'Bids editable until the round deadline, then automatically locked.',
  'GP Capital controls which counterparties see updated documents.',
  'Competing bid visibility remains at GP Capital&rsquo;s discretion only.',
  'Borrower identity revealed per approval after NDA where applicable.',
  'Activity is monitored and logged for compliance.',
];

const WORKFLOW = [
  { num: '1', text: 'Receive a deal invitation from GP Capital.' },
  { num: '2', text: 'Accept the NDA and access the deal room.' },
  { num: '3', text: 'Review materials, ask questions, request more information.' },
  { num: '4', text: 'Submit indicative terms via the structured bid form.' },
  { num: '5', text: 'Revise if permitted; await shortlist outcome.' },
];

// ── Building blocks ───────────────────────────────────────────────────────

const Section = ({ children, alt, testId, className = '' }) => {
  const [ref, visible] = useReveal({ threshold: 0.1 });
  return (
    <section
      ref={ref}
      className={`gpc-section ${alt ? 'gpc-section--alt' : ''} ${className}`}
      data-testid={testId}
    >
      <div className={`gpc-container gpc-reveal ${visible ? 'is-visible' : ''}`}>{children}</div>
    </section>
  );
};

const StepCard = ({ step, index }) => (
  <div className="gpc-card flex items-start gap-4" data-testid={`lender-step-${index + 1}`}>
    <div
      className="w-9 h-9 rounded-md flex items-center justify-center shrink-0 mt-0.5"
      style={{ background: 'rgba(201,168,106,0.10)' }}
    >
      <step.icon className="w-4 h-4" style={{ color: 'var(--gpc-gold)' }} />
    </div>
    <div className="min-w-0">
      <div className="flex items-baseline gap-2 mb-1.5">
        <span
          className="gpc-num text-[10px]"
          style={{ color: 'var(--gpc-muted)', letterSpacing: '0.2em' }}
        >
          {String(index + 1).padStart(2, '0')}
        </span>
        <h3 className="gpc-h3" style={{ fontSize: '16px' }}>{step.title}</h3>
      </div>
      <p className="gpc-body" style={{ fontSize: '14px', lineHeight: 1.6 }}>{step.body}</p>
    </div>
  </div>
);

// ── Page ──────────────────────────────────────────────────────────────────

export const ForLendersPage = () => {
  const [accessOpen, setAccessOpen] = useState(false);

  return (
    <div data-testid="for-lenders-page-content">
      {/* Hero */}
      <Section testId="lenders-hero" className="relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.55] pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(rgba(19,35,63,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(19,35,63,0.06) 1px, transparent 1px)',
            backgroundSize: '64px 64px',
            maskImage: 'radial-gradient(60% 70% at 75% 35%, #000 30%, transparent 80%)',
            WebkitMaskImage: 'radial-gradient(60% 70% at 75% 35%, #000 30%, transparent 80%)',
          }}
        />
        <div className="relative max-w-3xl">
          <p className="gpc-eyebrow mb-5">Private Capital · For Capital Partners</p>
          <h1
            className="mb-6"
            style={{
              fontFamily: 'Fraunces, "Playfair Display", Georgia, serif',
              fontWeight: 400,
              fontSize: 'clamp(2.2rem, 4.6vw, 3.8rem)',
              lineHeight: 1.05,
              letterSpacing: '-0.01em',
              color: 'var(--gpc-ink)',
            }}
          >
            Curated mandates, <span style={{ color: 'var(--gpc-gold)', fontStyle: 'italic' }}>controlled access.</span>
          </h1>
          <p className="gpc-body mb-9 max-w-2xl">
            Review NDA-gated opportunities matched to mandate appetite, respond with structured interest, and build a clearer view of private-market deal flow.
          </p>
          <div className="flex flex-wrap gap-3" data-testid="lenders-hero-ctas">
            <button
              type="button"
              onClick={() => setAccessOpen(true)}
              className="gpc-cta-primary"
              data-testid="lenders-cta-request-access"
            >
              Request access
              <ArrowRight className="w-4 h-4" />
            </button>
            <Link to="/portal/login" className="gpc-cta-secondary" data-testid="lenders-cta-platform">
              Enter platform
            </Link>
          </div>
        </div>
      </Section>

      {/* Positioning */}
      <Section alt testId="lenders-positioning">
        <div className="max-w-3xl">
          <p className="gpc-eyebrow mb-4">Why GP Capital</p>
          <h2 className="gpc-h2 mb-5">
            Institutional origination through a <span style={{ color: 'var(--gpc-gold)', fontStyle: 'italic' }}>controlled platform.</span>
          </h2>
          <div className="gpc-body space-y-4">
            <p>
              GP Capital provides institutional lenders and family offices with access to curated private credit and structured finance opportunities. Every transaction has been sourced, underwritten and packaged by GP Capital before it reaches a counterparty.
            </p>
            <p>
              You receive only opportunities matched to your stated mandate — asset class, geography, deal size and product type. The process is managed end-to-end through the Capital Access Platform — a secure environment designed for confidentiality, structured engagement and efficient execution.
            </p>
          </div>
        </div>
      </Section>

      {/* Platform controls */}
      <Section testId="lenders-controls">
        <div className="max-w-3xl mb-10">
          <p className="gpc-eyebrow mb-4">Platform controls</p>
          <h2 className="gpc-h2 mb-4">
            Designed for selective, confidential engagement.
          </h2>
          <p className="gpc-body max-w-2xl">
            Each transaction passes through the same controls before it reaches a capital partner — and stays within them through to close.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {PLATFORM_CONTROLS.map((c) => (
            <div key={c.title} className="gpc-card flex flex-col gap-3" data-testid={`lenders-control-${c.title.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
              <div
                className="w-9 h-9 rounded-md flex items-center justify-center"
                style={{ background: 'rgba(201,168,106,0.10)' }}
              >
                <c.icon className="w-4 h-4" style={{ color: 'var(--gpc-gold)' }} />
              </div>
              <h3 className="gpc-h3" style={{ fontSize: '15px' }}>{c.title}</h3>
              <p className="gpc-body" style={{ fontSize: '14px', lineHeight: 1.55 }}>{c.body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Pathway — labels match homepage exactly */}
      <Section alt testId="lenders-pathway">
        <div className="max-w-3xl mb-10">
          <p className="gpc-eyebrow mb-4">Capital partner pathway</p>
          <h2 className="gpc-h2 mb-4">
            How an opportunity moves through your view.
          </h2>
          <p className="gpc-body max-w-2xl">
            Every capital partner relationship follows the same five-step pattern — calibrated to your mandate, not a generic distribution list.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          {PATHWAY_STEPS.map((s, i) => <StepCard key={s.title} step={s} index={i} />)}
        </div>
      </Section>

      {/* Process notes + your workflow */}
      <Section testId="lenders-process">
        <div className="grid lg:grid-cols-2 gap-10">
          <div>
            <p className="gpc-eyebrow mb-4">Controlled process</p>
            <h2 className="gpc-h2 mb-5">
              GP Capital manages every round.
            </h2>
            <p className="gpc-body mb-6">
              Competing bids are hidden by default. Borrower identity may be withheld until after NDA acceptance or at GP Capital&apos;s discretion. The process may be single-round, multi-round, or include a best-and-final stage — all managed by GP Capital.
            </p>
            <ul className="space-y-3">
              {PROCESS_NOTES.map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-3 text-[14px]"
                  style={{ color: 'var(--gpc-ink)', opacity: 0.86, fontFamily: 'Inter, system-ui, sans-serif' }}
                >
                  <span
                    className="w-1 h-1 rounded-full shrink-0 mt-2"
                    style={{ background: 'var(--gpc-gold)' }}
                  />
                  <span dangerouslySetInnerHTML={{ __html: item }} />
                </li>
              ))}
            </ul>
          </div>

          <div
            className="gpc-card"
            style={{ background: 'var(--gpc-paper-2)' }}
            data-testid="lenders-workflow-card"
          >
            <div className="flex items-center gap-2 mb-5">
              <Users className="w-4 h-4" style={{ color: 'var(--gpc-gold)' }} />
              <h3 className="gpc-h3" style={{ fontSize: '14px' }}>Your workflow</h3>
            </div>
            <div className="space-y-5">
              {WORKFLOW.map((s) => (
                <div key={s.num} className="flex items-start gap-4">
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
                    style={{ background: 'rgba(201,168,106,0.14)' }}
                  >
                    <span
                      className="gpc-num text-[12px]"
                      style={{ color: 'var(--gpc-gold)', fontWeight: 500 }}
                    >
                      {s.num}
                    </span>
                  </div>
                  <span
                    className="text-[14px]"
                    style={{ color: 'var(--gpc-ink)', opacity: 0.9, fontFamily: 'Inter, system-ui, sans-serif' }}
                  >
                    {s.text}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* Closing CTA */}
      <Section alt testId="lenders-cta">
        <div className="max-w-2xl">
          <p className="gpc-eyebrow mb-4">Access the platform</p>
          <h2 className="gpc-h2 mb-4">
            Approved counterparties review assigned opportunities here.
          </h2>
          <p className="gpc-body mb-3">
            Approved counterparties can log in to the Capital Access Platform to review assigned deal opportunities.
          </p>
          <p
            className="mb-8 text-[12px]"
            style={{
              color: 'var(--gpc-muted)',
              fontFamily: 'Space Grotesk, system-ui, sans-serif',
              letterSpacing: '0.04em',
            }}
          >
            All opportunities are indicative, subject to underwriting, diligence, approvals and definitive documentation.
          </p>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => setAccessOpen(true)}
              className="gpc-cta-primary"
              data-testid="for-lenders-request-access-cta"
            >
              Request access
              <ArrowRight className="w-4 h-4" />
            </button>
            <Link to="/portal/login" className="gpc-cta-secondary" data-testid="for-lenders-login-cta">
              Enter platform
            </Link>
          </div>
        </div>
      </Section>

      <RequestAccessModal open={accessOpen} onClose={() => setAccessOpen(false)} />

      <StickyMobileCTA
        label="Request Access"
        testid="sticky-request-access"
        onClick={() => setAccessOpen(true)}
      />
    </div>
  );
};

export default ForLendersPage;
